"use client";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Chip, IconButton, Switch } from "@mui/material";
import React from "react";

import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { USERS_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useUserData from "@/hooks/data/use-user-data";
import useUpdateUser from "@/hooks/requests/user/use-update-user";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import IUser from "@/interfaces/user.interface";
import { UsersMessages } from "@/messages/users/user.messages";
import { UserStatus } from "@/types/user.types";

import { UserPopover } from "../UserPopover";
interface Props {
  user: IUser;
  messages: UsersMessages;
  index: number;
  currentUser: IUser;
}

export function UserTableRow({ user, index, messages, currentUser }: Props) {
  const { name, email, phone1, isActive, isEmailVerified } = useUserData({ user });
  const { mutate } = useMutate();
  const { update, isLoading: isLoadingUpdate } = useUpdateUser();
  const {
    openPopover,
    anchorEl,
    closePopover,
    isOpen,
    selected: selectedUser,
  } = usePopOver<IUser>();

  const hasNoPermissionToEdit = useHasNoPermission(USERS_PERMISSIONS.EDIT);

  const {
    emailNotVerified,
    deactivatedUserSuccess,
    activatedUserSuccess,
    deactivatedUserError,
    activatedUserError,
  } = messages;

  const handleToggleActive = async (user: IUser, isActive: boolean) => {
    const status = isActive ? UserStatus.Inactive : UserStatus.Active;
    const successMessage = isActive ? deactivatedUserSuccess : activatedUserSuccess;
    const errorMessage = isActive ? deactivatedUserError : activatedUserError;
    const _id = user.id;
    const formData = { ...user, status };
    const onSuccess = () => mutate(SWR_KEYS.getUsers);

    await update({ _id, formData, errorMessage, onSuccess });
  };

  const disableSwitch = currentUser?.id === user.id || isLoadingUpdate || hasNoPermissionToEdit;

  return (
    <TableRow index={index}>
      <TableCell>{name}</TableCell>
      <TableCell>{email}</TableCell>
      <TableCell>{phone1}</TableCell>

      <TableCell>
        {isEmailVerified ? (
          <Switch
            checked={isActive}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(user, isActive);
            }}
            disabled={disableSwitch}
          />
        ) : (
          <Chip label={emailNotVerified} color="warning" />
        )}
      </TableCell>
      <TableCell>
        <IconButton onClick={(e) => openPopover(e, user)}>
          <MoreHorizIcon />
        </IconButton>
        {selectedUser && (
          <UserPopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            selectedUser={selectedUser}
          />
        )}
      </TableCell>
    </TableRow>
  );
}
